// Relative URLs
import { usePathname, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { IS_C1_SEARCH_PARAM, useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';

export function pathTopicAndActivity(pathname: string) {
    const tidiedPathname = pathname.trim().startsWith('/') ? pathname.trim().substring(1) : pathname.trim();
    const pathElements = tidiedPathname.split('/');
    if(pathElements.length < 2) return {};
    if(pathElements[0] !== 'activities') return {};
    const activityKey = pathElements[1];
    if(pathElements.length > 2) {
        return { activityKey, topicId: pathElements[2] };
    } else {
        return { activityKey };
    }
}

export const useUrls = () => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const isC1Integrated = useIsC1Integrated();

    const pathWithSearchString = useMemo(() => {
        const searchString = searchParams?.toString();
        return pathname + (searchString ? "?" + searchString : "");
    }, [searchParams, pathname]);

    const pathIds = useMemo(() => {
        return pathTopicAndActivity(pathname);
    }, [pathname]);

    const URLS = useMemo(() => {
        const c1ParamOnly = isC1Integrated ? `?${IS_C1_SEARCH_PARAM}=true` : '';
        const c1ParamAnd = isC1Integrated ? `&${IS_C1_SEARCH_PARAM}=true` : '';

        return {
            chatLogBack: (activity: string, topicId: string) => `/activities/${activity}/${topicId}/feedback${c1ParamOnly}`,
            chatLogLink: (activity: string, topicId: string) => `/activities/${activity}/${topicId}/chatlog${c1ParamOnly}`,
            conversationPage: (activity: string, topicId: string, subtopic?: string | null) => {
                const queryString = subtopic ? '?subtopic=' + subtopic + c1ParamAnd : c1ParamOnly;
                return `/activities/${activity}/${topicId}/chat${queryString}`;
            },
            feedbackContinue: (activity: string, topicId: string) => `/activities/${activity}/${topicId}/chat?continue=true${c1ParamAnd}`,
            feedbackFinishButtonLink: () => `/activities${c1ParamOnly}`,
            checksPage: () => `/activities/${pathIds.activityKey}/${pathIds.topicId}/checks${c1ParamOnly}`,
            transcriptCheckPage:()=>`/activities/${pathIds.activityKey}/${pathIds.topicId}/transcriptcheck${c1ParamOnly}`,
            topicSelectLink: (activity: string, topic: string) => `/activities/${activity}/${topic}${c1ParamOnly}`,
            topicSelectBack: () => `/activities${c1ParamOnly}`,
            chatlogReportLink: () => `/report?backUrl=${encodeURIComponent(pathWithSearchString)}${c1ParamAnd}`,
            wellDoneRedirectFeedback: (activity: string, topicId: string) => `/activities/${activity}/${topicId}/feedback${c1ParamOnly}`,
            defaultEndingRedirect: (activity: string, topicId: string) => `/activities/${activity}/${topicId}/welldone${c1ParamOnly}`,
            warmupEndingRedirection: () => `/activities/${pathIds.activityKey}/${pathIds.topicId}${c1ParamOnly}`,
            errorModalRestart: (userCompletedMicChecks: boolean) => userCompletedMicChecks? `/activities${c1ParamOnly}` : `/${c1ParamOnly}`,
            tipsBackToTopic: (activity?: string) => `/activities/${activity}${c1ParamOnly}`,
            activitiesToTopics: (activityTextDataUrl: string) => `${activityTextDataUrl}${c1ParamOnly}`,
            checksCompleteRedirect: (hasDoneWarmup: boolean, activity: string, topicId: string) => hasDoneWarmup ? `/activities/${activity}/${topicId}${c1ParamOnly}` : `/activities/${activity}/${topicId}/warmup${c1ParamOnly}`,
            defaultReportBackLink: () => `/activities${c1ParamOnly}`,
            appStartButton: () => `/activities${c1ParamOnly}`,
            warmup: () => `/activities/${pathIds.activityKey}/${pathIds.topicId}/warmup${c1ParamOnly}`
        }
    }, [pathWithSearchString, isC1Integrated, pathIds]);
    return URLS;
};
